import _ from 'lodash'
import {toObjectIdMap, ID_SEPARATOR} from "./props-utils";
import {isObject} from "./mobx-utils";
import { BackendErrorMessage, FormBean, IDomainData, IUIComponentDef } from '../datatypes';

export interface BindedComponentError {
    error?: string,
    code?: string,
    domainObject?: IDomainData,
    attrName?: string,
    formBean?: FormBean,
    objectName?: string,
    objectId?: string,
    metadata?: IUIComponentDef | {label:string} //TODO: what is it ??? used by PaymentMethod component
}


export function replaceUsingActionMessage(errCode:string, err:string, formBean:FormBean){
    if(formBean.actionMessages && formBean.actionMessages[errCode]) return formBean.actionMessages[errCode]
    return err;
}

export function getError(domainObject:any, attributeName:string, metadata:any) {
    if(domainObject._errors && attributeName && domainObject._errors[attributeName]) {
        let errMessage = domainObject._errors[attributeName].message;
        errMessage = metadata ? replaceErrorMessage(errMessage, metadata) : errMessage;

        return errMessage ? errMessage : '';
    }
    return null;
}

export function parseError(errMessage:string, metadata:any) {
    errMessage = metadata ? replaceErrorMessage(errMessage, metadata) : errMessage;
    return errMessage;
}
/*
Giving an errorMessages & metadata it parses the error.
Eg: replaces {attribute.label} with appropriate label.
 */
function replaceErrorMessage(errorMessage:string, metadata:any){
    let label = metadata.label;
    if (label && errorMessage && errorMessage.includes('{attribute.label}'))
        errorMessage = errorMessage.replace('{attribute.label}', label);

    return errorMessage;
}

/*
This method is used to add attribute level errors & calls addErrorToCache method.
attribute level errors are stored in domainObjet._errors[attrName1.err, attrNAme2.err]
 */
export function addError(errorProps:BindedComponentError) {
    let domainObject = errorProps.domainObject;
    if(domainObject){  
        domainObject._errors = domainObject._errors ? domainObject._errors : {};
        if(errorProps.attrName) domainObject._errors![errorProps.attrName] = {
            message:parseError(errorProps.error!, errorProps.metadata), 
            code: errorProps.code ? errorProps.code : ''
        };
    }
    addErrorToCache(errorProps.formBean, domainObject, errorProps.objectName, errorProps.attrName, errorProps.error, errorProps.metadata);
}

/*
This method is used to add errors to the errorCache. So that errorCache will always be in sync whenever frontend error happens.
Eg: errorCache is first filled when backend responds with errorMessage.
At this point, whenever a frontend error occurs, it has to be added to the cache, so whenever backend response/errorCache overrides, it will
also be in sync
 */
export function addErrorToCache(formBean:any, domainObject:any, objectName:string|undefined, attrName:string|undefined, err:any, metadata:any){
    if(!domainObject || !attrName || !objectName || !formBean) return;

    if(!formBean.errorCache.errorMessage){ 
        formBean.errorCache.errorMessage = [];
    }
    
    formBean.errorCache.errorMessage.push({objectId: domainObject.Id, attributeName:attrName, message: parseError(err, metadata), objectName: objectName});
}

export function addContextualError(formBean:any, errCode:any, errMsg:any){
    formBean.contextualErrors[errCode] = errMsg;
}

export function removeContextualError(formBean:any, errCode:string){
    if(formBean.contextualErrors.hasOwnProperty(errCode)) delete formBean.contextualErrors[errCode];
}

export const removeErrors = (obj:any) => {
    Object.keys(obj).forEach(key => {
        if (obj[key] && typeof obj[key] === 'object') removeErrors(obj[key]);
        delete obj._errors;
    });
    return obj;
};

export const printErrors = (obj:any, keys:string[])=> {
    Object.keys(obj).forEach(key => {
        if(key === '_errors'){
            return;
        }
        let newKeys = keys.slice();
        newKeys.push(key);

        if(obj._errors) console.warn(`${newKeys}._errors=${JSON.stringify(obj._errors)}`);
        if (obj[key] && typeof obj[key] === 'object') printErrors(obj[key], newKeys);
    });
}

export function clearAllErrors(formBean:any) {
    formBean.contextualErrors = {};
    formBean.errorCache = {};
    removeErrors(formBean);
}

/*
This method is used to clear attribute level errors.
It also calls clearErrorFromCache method
 */
export function clearError(errorProps:BindedComponentError, clearAll?:boolean) {
    let domainObject = errorProps.domainObject;
    if(!domainObject) return;

    if(errorProps.attrName && domainObject._errors && domainObject._errors[errorProps.attrName]){
        domainObject._errors[errorProps.attrName] = null;
    }
    clearErrorFromCache(errorProps.formBean, errorProps.attrName!, errorProps.objectId, clearAll)
}

/*
This method is used to clear an error from errorCache so it will always be in sync.
Since errors in errorCache (which is a copy of backend errors) overrides frontend, its necessary to clear error from the errorCache as well.
 */
export function clearErrorFromCache(formBean:any, attrName:string, objectId:string|undefined, clearAll:any){
    if(!formBean || (!attrName && !clearAll) || !objectId) return;
    if(formBean.errorCache.errorMessage && !_.isEmpty(formBean.errorCache.errorMessage)) {
        if (clearAll) {
            //clears all the errors inside the domainObject (uses objectId)
            formBean.errorCache.errorMessage = formBean.errorCache.errorMessage.filter((e:any) => e.objectId !== objectId);
        } else {
            formBean.errorCache.errorMessage = formBean.errorCache.errorMessage.filter((e:any) => e.objectId !== objectId || e.attributeName !== attrName)
            let nonContextualErrors = formBean.errorCache.errorMessage.filter((e:any) => e.attributeName != null && e.attributeName !== '');
            if (nonContextualErrors && nonContextualErrors.length === 0) removeContextualError(formBean, 'formError');
        }
    }

}

export function parseAndMergeErrors(formBean:any, errorMessage:BackendErrorMessage[]) {
    /*
    2 types of errors.
    1 - contextual errors which dont have attributeName
    2 - attribute level errors
     */
    let objectIdMap = toObjectIdMap(formBean.DomainObject);
    let nonContextualErrors = errorMessage && errorMessage.filter(e =>e.attributeName != null && e.attributeName !== '');
    if(nonContextualErrors && nonContextualErrors.length > 0){
        addContextualError(formBean, 'formError', 'There are errors on the form, please fix the errors and try again.')
    }

    let contextualErrors = errorMessage && errorMessage.filter(e => e.attributeName == null || e.attributeName === '' )
    contextualErrors && contextualErrors.length > 0 && contextualErrors.map (e => {
        let code = e.code;
        addContextualError(formBean, code, e.message);
    });

    let objectIdBasedErrors = toObjectIdEdgeNameMapErrors(nonContextualErrors)
    mergeErrorsBasedOnId(formBean, objectIdBasedErrors, objectIdMap);
}

/**
 * Merges errors to the relevant domain objects based on their ID
 * @param errorMessages array of msgs returned from service call
 * @param objectIdMap id to object map created using toObjectIdMap
 */
export function mergeErrorsBasedOnId(formBean:any, objectIdBasedErrors:any, objectIdMap:any) {
    for(let id in objectIdBasedErrors) {
        if(!objectIdMap[id]) {
            // still showing error when id does not match
            for(let name in objectIdBasedErrors[id]) {
                addContextualError(formBean, objectIdBasedErrors[id][name].code, objectIdBasedErrors[id][name].message);
            }
        } else {
            objectIdMap[id]._errors = objectIdBasedErrors[id];
        }
    }
}

function toObjectIdEdgeNameMapErrors(errorMessages:BackendErrorMessage[]) {
    let obj = {} as {[key:string]: {[key:string]:BackendErrorMessage}  };
    errorMessages.forEach(msg => {
        let IdWithEdgeName = msg.objectName + ID_SEPARATOR + msg.objectId;
        if(!obj[IdWithEdgeName]) {
            obj[IdWithEdgeName] = {}
        }
        obj[IdWithEdgeName][msg.attributeName!] = msg;
    });
    return obj;
}

/*
@deprecated to be removed later
 */
function toObjectIdMapErrors(errorMessages: any[]) {
    let obj = {}  as {[key:string]:any};
    errorMessages.forEach(msg => {
        if (!obj[msg.objectId]) {
            obj[msg.objectId] = {}
        }
        obj[msg.objectId][msg.attributeName] = msg;
    });
    return obj;
}


/**
 * @deprecated use mergeErrorsBasedOnId instead
 * @param errorObj
 * @param obj
 */
function mergeErrors(errorObj:any, obj:any) {
    for(let objName in errorObj) {
        if(Array.isArray(obj[objName])) {
            for(let idx in errorObj[objName]) {
                if(errorObj[objName][idx]) {
                    obj[objName][idx]._errors = errorObj[objName][idx];
                    mergeErrors(errorObj[objName][idx], obj[objName][idx])
                }
            }
        } else if(isObject(obj[objName])){
            obj[objName]._errors = errorObj[objName];
            mergeErrors(errorObj[objName], obj[objName]);
        }
    }
}